import React, { useEffect, useRef, useState } from 'react'
import { Animated, Easing, View, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native'
import { appColors, appFonts, fontSize } from '../../themes/appTheme'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { getOneStaffHistoryDetails } from '../../redux/slices/staffSlice'
import { ZenIcon, ZenText, ZenToast } from 'zen-core-elements'

interface TH {
    staffId: number,
    onClose: () => void,
    staffName: string
}

type TargetKey = 'daily_target' | 'weekly_target' | 'monthly_target';

type TargetItem = {
    title: string;
    icon: string;
    target: number;
    progress: number;
    key: TargetKey;
};

const initialTargets: TargetItem[] = [
    { title: 'Daily Target', icon: 'calendar-today', target: 0, progress: 0, key: 'daily_target' },
    { title: 'Weekly Target', icon: 'calendar-week', target: 0, progress: 0, key: 'weekly_target' },
    { title: 'Monthly Target', icon: 'calendar-month', target: 0, progress: 0, key: 'monthly_target' },
]

const ACHIEVED_COLOR = '#0ca85a'

const TargetHistory = ({ staffId, onClose, staffName }: TH) => {

    const dispatch = useAppDispatch()
    const format = (val: number | undefined) => (val ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

    const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '';

    const [isLoading, setIsLoading] = useState(false)

    const [targets, setTargets] = useState<TargetItem[]>(initialTargets);

    const progressRefs = useRef(initialTargets.map(() => new Animated.Value(0))).current;

    useEffect(() => {
        if (staffId > 0) {
            setIsLoading(true)
            getOneStaffHistory()
        }
    }, [staffId])

    const getOneStaffHistory = async () => {
        dispatch(getOneStaffHistoryDetails({ staffId: staffId })).unwrap().then((res) => {
            const history = res?.posStaffTargetHistory?.[0] as Record<string, any> | undefined;
            // No history: show empty targets rather than the previously viewed staff's values
            setTargets(initialTargets.map((item) => {
                const rawTarget = history?.[item.key];
                const rawProgress = history?.[`achieved_${item.key}`];
                return {
                    ...item,
                    target: isNaN(Number(rawTarget)) ? 0 : Number(rawTarget),
                    progress: isNaN(Number(rawProgress)) ? 0 : Number(rawProgress),
                };
            }));
        }).catch((error) => {
            ZenToast({ type: 'info', message: 'Something went wrong , Please try again later' })
            onClose()
            console.log("error in the getOneStaffHistoryDetails-->", error);
        }).finally(() => {
            setIsLoading(false)
        })
    }

    //Percentage of the target achieved, a target of 0 means no target has been set
    const getPercentage = (item: TargetItem) => item.target > 0 ? Math.max((item.progress / item.target) * 100, 0) : 0

    //Animate the progress bars whenever the target values are loaded
    useEffect(() => {
        if (isLoading) return
        progressRefs.forEach((ref) => ref.setValue(0))
        const animations = progressRefs.map((ref, index) => Animated.timing(ref, {
            toValue: Math.min(getPercentage(targets[index]), 100),
            duration: 700,
            useNativeDriver: false, // Width cannot be driven natively
            easing: Easing.out(Easing.cubic),
        }));
        const animation = Animated.stagger(150, animations)
        animation.start();
        return () => animation.stop()
    }, [targets, isLoading])

    return (
        <View style={styles.container}>
            {/* Header */}
            <View style={styles.header}>
                <View style={styles.headerIcon}>
                    <ZenIcon name='target' type='material-community' size={22} color={appColors.primary} />
                </View>
                <View style={styles.headerText}>
                    <ZenText content='Sales Targets' style={styles.title} />
                    {staffName ? <ZenText content={staffName} style={styles.subtitle} numberOfLines={1} /> : null}
                </View>
                <TouchableOpacity onPress={onClose} style={styles.closeButton} accessibilityRole='button' accessibilityLabel='Close'>
                    <ZenIcon name='close' type='material-community' size={20} color={appColors.labelColor} />
                </TouchableOpacity>
            </View>

            {isLoading ?
                <View style={styles.loader}>
                    <ActivityIndicator size={'small'} color={appColors.primary} />
                    <ZenText content='Loading targets...' style={styles.helperText} />
                </View> :
                <View style={styles.body}>
                    {targets.map((item, index) => {
                        const hasTarget = item.target > 0
                        const percentage = getPercentage(item)
                        const isAchieved = hasTarget && percentage >= 100
                        const remaining = Math.max(item.target - item.progress, 0)
                        const accentColor = isAchieved ? ACHIEVED_COLOR : appColors.primary
                        return (
                            <View key={item.key} style={styles.card}>
                                <View style={styles.cardHeader}>
                                    <View style={styles.cardTitle}>
                                        <ZenIcon name={item.icon} type='material-community' size={18} color={appColors.primary} />
                                        <ZenText content={item.title} style={styles.cardTitleText} />
                                    </View>
                                    {hasTarget ?
                                        <View style={[styles.badge, { backgroundColor: isAchieved ? appColors.lightTealGreen : appColors.tealPrimary }]}>
                                            {isAchieved ? <ZenIcon name='check-circle' type='material-community' size={13} color={ACHIEVED_COLOR} /> : null}
                                            <ZenText content={isAchieved ? 'Achieved' : `${Math.floor(percentage)}%`} style={[styles.badgeText, { color: accentColor }]} />
                                        </View> :
                                        <View style={[styles.badge, { backgroundColor: appColors.coolGray }]}>
                                            <ZenText content='No target set' style={[styles.badgeText, { color: appColors.labelColor }]} />
                                        </View>}
                                </View>

                                <View style={styles.progressTrack}>
                                    <Animated.View
                                        style={[styles.progressFill, {
                                            backgroundColor: accentColor,
                                            width: progressRefs[index].interpolate({
                                                inputRange: [0, 100],
                                                outputRange: ['0%', '100%'],
                                            }),
                                        }]}
                                    />
                                </View>

                                <View style={styles.amountRow}>
                                    <View>
                                        <ZenText content='Achieved' style={styles.amountLabel} />
                                        <ZenText content={`${currencySymbol}${format(item.progress)}`} style={[styles.amountValue, { color: accentColor }]} />
                                    </View>
                                    <View style={{ alignItems: 'flex-end' }}>
                                        <ZenText content='Target' style={styles.amountLabel} />
                                        <ZenText content={hasTarget ? `${currencySymbol}${format(item.target)}` : '-'} style={styles.amountValue} />
                                    </View>
                                </View>
                                {hasTarget && !isAchieved ?
                                    <ZenText content={`${currencySymbol}${format(remaining)} more to reach the target`} style={styles.helperText} /> : null}
                            </View>
                        )
                    })}
                </View>
            }
        </View>
    )
}

const styles = StyleSheet.create({
    container: {
        backgroundColor: appColors.light,
        borderRadius: 16,
        overflow: 'hidden',
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingHorizontal: 16,
        paddingVertical: 14,
        borderBottomWidth: 1,
        borderBottomColor: appColors.lightBackgroundColor,
    },
    headerIcon: {
        width: 40,
        height: 40,
        borderRadius: 20,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: appColors.tealPrimary,
    },
    headerText: {
        flex: 1,
    },
    title: {
        fontFamily: appFonts.bold,
        fontSize: fontSize.mediumFontSize,
        color: appColors.primary,
    },
    subtitle: {
        fontFamily: appFonts.medium,
        fontSize: fontSize.smallFontSize,
        color: appColors.labelColor,
        marginTop: 2,
        textTransform: 'capitalize',
    },
    closeButton: {
        padding: 8,
        borderRadius: 20,
        backgroundColor: appColors.lightBackgroundColor,
    },
    loader: {
        minHeight: 160,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
    body: {
        padding: 16,
        gap: 12,
    },
    card: {
        padding: 14,
        borderRadius: 12,
        backgroundColor: appColors.lightBackgroundColor,
    },
    cardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
    },
    cardTitle: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flex: 1,
    },
    cardTitleText: {
        fontFamily: appFonts.bold,
        fontSize: fontSize.smallFontSize,
        color: appColors.lightDark,
    },
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 3,
        paddingHorizontal: 10,
        borderRadius: 12,
    },
    badgeText: {
        fontFamily: appFonts.bold,
        fontSize: fontSize.badgeFontSize,
    },
    progressTrack: {
        width: '100%',
        height: 8,
        borderRadius: 4,
        backgroundColor: appColors.coolGray,
        overflow: 'hidden',
        marginVertical: 12,
    },
    progressFill: {
        height: 8,
        borderRadius: 4,
    },
    amountRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
    },
    amountLabel: {
        fontFamily: appFonts.medium,
        fontSize: fontSize.badgeFontSize,
        color: appColors.labelColor,
        marginBottom: 2,
    },
    amountValue: {
        fontFamily: appFonts.bold,
        fontSize: fontSize.smallFontSize,
        color: appColors.lightDark,
    },
    helperText: {
        fontFamily: appFonts.medium,
        fontSize: fontSize.badgeFontSize,
        color: appColors.labelColor,
        marginTop: 8,
    },
});


export default TargetHistory
